import React, { useState } from 'react';
import { GripVertical, Lock, Pencil, Trash2, Plus, Columns3 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { BoardColumn, ColumnType, generateColumnId } from './boardColumns';

/**
 * Réglage des colonnes d'UN tableau (maquette 16c).
 *
 * Tout se joue localement puis part en un seul enregistrement : réordonner, renommer et masquer
 * colonne par colonne en écrivant à chaque geste aurait multiplié les allers-retours réseau pour un
 * réglage qu'on ajuste par tâtonnement.
 *
 * Les colonnes verrouillées (Client, Heure, Adultes, Enfants) ne sont ni supprimables ni masquables :
 * sans elles une ligne de réservation ne veut plus rien dire.
 */

const TYPE_LABELS: Record<ColumnType, string> = {
    texte: 'Texte', nombre: 'Nombre', montant: 'Montant', heure: 'Heure', choix: 'Choix', personne: 'Personne',
};

/** Les six types, avec les textes de la maquette — ce sont eux qui évitent la question « ça sert à quoi ? ». */
const TYPE_HELP: Array<[ColumnType, string]> = [
    ['texte', 'Nom, commentaire, allergies. Libre.'],
    ['nombre', 'Couverts, quantités. Additionné en pied de tableau.'],
    ['montant', "Entre dans l'encaissé et le reste à payer."],
    ['heure', 'Format 20:30, triable.'],
    ['choix', 'Une liste que vous définissez : menu, formule, statut.'],
    ['personne', "Pris par, encaissé par. Puise dans vos noms d'équipe."],
];

/** Un badge par type, sur les couleurs d'état du thème plutôt que six teintes inventées ici. */
const TYPE_BADGE: Record<ColumnType, string> = {
    texte: 'bg-surface-soft border-border text-content-muted',
    nombre: 'bg-state-info-soft border-state-info/30 text-state-info',
    montant: 'bg-state-ok-soft border-state-ok/30 text-state-ok',
    heure: 'bg-surface-soft border-border text-content-muted',
    choix: 'bg-accent-soft border-accent-border text-accent-text',
    personne: 'bg-state-warn-soft border-state-warn/30 text-state-warn',
};

export function BoardColumnsModal({
    boardTitle, columns, onClose, onSave, saving,
}: {
    boardTitle: string;
    columns: BoardColumn[];
    onClose: () => void;
    onSave: (columns: BoardColumn[]) => void;
    saving: boolean;
}) {
    const [draft, setDraft] = useState<BoardColumn[]>(columns);
    const [newLabel, setNewLabel] = useState('');
    const [newType, setNewType] = useState<ColumnType>('montant');
    const [dragIndex, setDragIndex] = useState<number | null>(null);

    const move = (from: number, to: number) => {
        if (to < 0 || to >= draft.length) return;
        setDraft((prev) => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    const toggleVisible = (id: string) =>
        setDraft((prev) => prev.map((c) => (c.id === id && !c.locked ? { ...c, visible: !c.visible } : c)));

    const rename = (id: string) => {
        const col = draft.find((c) => c.id === id);
        if (!col) return;
        const label = window.prompt('Nouvel intitulé de la colonne', col.label);
        if (!label || !label.trim()) return;
        // L'id ne change jamais au renommage : c'est lui qui relie la colonne aux valeurs déjà saisies.
        setDraft((prev) => prev.map((c) => (c.id === id ? { ...c, label: label.trim() } : c)));
    };

    const remove = (id: string) => setDraft((prev) => prev.filter((c) => c.id !== id || c.locked));

    const add = () => {
        const label = newLabel.trim();
        if (!label) return;
        setDraft((prev) => [...prev, {
            id: generateColumnId(label, prev), label, type: newType, visible: true, locked: false,
            ...(newType === 'choix' ? { options: [] } : {}),
        }]);
        setNewLabel('');
    };

    return (
        <Modal
            open
            onClose={onClose}
            widthPx={580}
            icon={<Columns3 size={17} />}
            title="Colonnes du tableau"
            subtitle={<>Glissez pour réordonner. Ces réglages ne valent que pour «&nbsp;{boardTitle}&nbsp;».</>}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Annuler</Button>
                    <Button onClick={() => onSave(draft)} disabled={saving}>
                        {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </Button>
                </>
            }
        >
            <div className="space-y-1.5 mb-4">
                {draft.map((col, index) => (
                    <div
                        key={col.id}
                        draggable={!col.locked}
                        onDragStart={() => setDragIndex(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => { if (dragIndex !== null) move(dragIndex, index); setDragIndex(null); }}
                        className={`flex items-center gap-2.5 flex-wrap px-3 py-2.5 rounded-[10px] border transition-colors ${
                            col.visible
                                ? 'bg-surface-soft border-border'
                                // Une colonne masquée s'efface visuellement au lieu de disparaître : on doit
                                // pouvoir la retrouver pour la réafficher.
                                : 'bg-surface border-border-soft opacity-60'
                        } ${col.locked ? '' : 'cursor-grab active:cursor-grabbing'}`}
                    >
                        <GripVertical size={14} className={col.locked ? 'text-content-muted/40' : 'text-content-muted'} />
                        <span className={`flex-1 min-w-[90px] text-[12.5px] font-semibold truncate ${col.visible ? 'text-content' : 'text-content-muted'}`}>
                            {col.label}
                        </span>
                        <span className={`inline-flex items-center h-[21px] px-2 rounded-full border text-[9.5px] font-bold uppercase tracking-wider shrink-0 ${TYPE_BADGE[col.type]}`}>
                            {TYPE_LABELS[col.type]}
                        </span>
                        {col.locked ? (
                            <span title="Colonne indispensable, non supprimable" className="flex shrink-0 text-content-muted">
                                <Lock size={13} />
                            </span>
                        ) : (
                            <span className="flex items-center gap-0.5 shrink-0">
                                <button type="button" title="Renommer" onClick={() => rename(col.id)}
                                    className="w-[26px] h-[26px] flex items-center justify-center rounded-[7px] text-content-muted hover:bg-surface hover:text-content transition-colors">
                                    <Pencil size={12} />
                                </button>
                                <button type="button" title="Supprimer" onClick={() => remove(col.id)}
                                    className="w-[26px] h-[26px] flex items-center justify-center rounded-[7px] text-state-danger/80 hover:bg-state-danger-soft hover:text-state-danger transition-colors">
                                    <Trash2 size={12} />
                                </button>
                            </span>
                        )}
                        <button
                            type="button"
                            disabled={col.locked}
                            title={col.locked ? 'Toujours affichée' : col.visible ? 'Masquer la colonne' : 'Afficher la colonne'}
                            onClick={() => toggleVisible(col.id)}
                            className={`w-9 h-[21px] shrink-0 rounded-full flex items-center px-[3px] transition-colors disabled:opacity-40 ${
                                col.visible ? 'bg-brand-600 justify-end' : 'bg-border justify-start'
                            }`}
                        >
                            <span className="w-[15px] h-[15px] rounded-full bg-white" />
                        </button>
                    </div>
                ))}
            </div>

            <div className="p-3.5 rounded-[11px] bg-surface-soft border border-border-soft">
                <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-3">Nouvelle colonne</div>
                <div className="flex items-end gap-2 flex-wrap">
                    <div className="flex-1 min-w-[130px]">
                        <label className="block mb-1.5 text-[11px] font-semibold text-content">Intitulé</label>
                        <input
                            className="field-input w-full"
                            placeholder="Ex: Allergies, Acompte, Arrivé"
                            value={newLabel}
                            onChange={(e) => setNewLabel(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && add()}
                        />
                    </div>
                    <div className="w-[140px] shrink-0">
                        <label className="block mb-1.5 text-[11px] font-semibold text-content">Type</label>
                        <select className="field-select w-full" value={newType} onChange={(e) => setNewType(e.target.value as ColumnType)}>
                            {TYPE_HELP.map(([type]) => <option key={type} value={type}>{TYPE_LABELS[type]}</option>)}
                        </select>
                    </div>
                    <Button onClick={add} disabled={!newLabel.trim()} className="shrink-0">
                        <Plus size={13} /> Ajouter
                    </Button>
                </div>

                <div className="mt-3.5 pt-3 border-t border-border-soft">
                    <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-2">Les six types</div>
                    <div className="flex flex-col gap-1.5">
                        {TYPE_HELP.map(([type, desc]) => (
                            <div key={type} className="flex items-baseline gap-2.5 flex-wrap">
                                <span className={`inline-flex items-center h-5 px-2 rounded-full border text-[9.5px] font-bold uppercase tracking-wider shrink-0 ${TYPE_BADGE[type]}`}>
                                    {TYPE_LABELS[type]}
                                </span>
                                <span className="flex-1 min-w-[140px] text-[11.5px] leading-snug text-content-muted">{desc}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
}
